import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  ShoppingCart, 
  Key, 
  Wrench, 
  Users, 
  Settings, 
  HandCoins,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  Star,
  Phone
} from 'lucide-react';
import AnimatedSection from '../components/common/AnimatedSection';
import ContactForm from '../components/common/ContactForm';
import SEOHead from '../components/common/SEOHead';
import { 
  companyInfo, 
  aboutContent, 
  services, 
  products, 
  differentials,
  testimonials,
  brands,
  heroImages,
  heroStats
} from '../data/mock';

const iconMap = {
  ShoppingCart,
  Key,
  Wrench,
  Users,
  Settings,
  HandCoins
};

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = heroImages.home;

  // Auto-rotate carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <>
      <SEOHead 
        title="Venda e Locação de Empilhadeiras"
        description={`${companyInfo.name} - Líder em venda, locação e manutenção de empilhadeiras em Saltinho/SP. ${companyInfo.experience}. Empilhadeiras diesel, GLP, elétricas e transpaletes.`}
      />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-gray-900">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900/30"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <AnimatedSection animation="fadeRight" delay={0}>
                <span className="inline-flex items-center gap-2 bg-teal-500/20 text-teal-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Star size={16} className="fill-teal-400" />
                  {companyInfo.experience}
                </span>
              </AnimatedSection>
              
              <AnimatedSection animation="fadeRight" delay={100}>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  {companyInfo.slogan.split(' ').slice(0, 2).join(' ')}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-teal-500"> {companyInfo.slogan.split(' ').slice(2).join(' ')}</span>
                </h1>
              </AnimatedSection>
              
              <AnimatedSection animation="fadeRight" delay={200}>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  {aboutContent.shortDescription}
                </p>
              </AnimatedSection>
              
              <AnimatedSection animation="fadeRight" delay={300}>
                <div className="flex flex-wrap gap-4 mb-12">
                  <Link
                    to="/produtos"
                    className="group bg-gradient-to-r from-teal-500 to-teal-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 hover:scale-105 transition-all duration-300 flex items-center gap-2"
                    data-testid="hero-products-btn"
                  >
                    Ver Produtos
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/locacao"
                    className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-semibold border border-white/20 hover:bg-white/20 hover:scale-105 transition-all duration-300"
                    data-testid="hero-rental-btn"
                  >
                    Locação
                  </Link>
                </div>
              </AnimatedSection>
              
              <AnimatedSection animation="fadeUp" delay={400}>
                <div className="flex items-center gap-8 text-gray-400">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={20} className="text-teal-500" />
                    <span>Venda</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={20} className="text-teal-500" />
                    <span>Locação</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={20} className="text-teal-500" />
                    <span>Manutenção</span>
                  </div>
                </div>
              </AnimatedSection>
            </div>
            
            {/* Hero Image Carousel with Stats */}
            <AnimatedSection animation="fadeLeft" delay={200} className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-teal-500/20 to-teal-600/20 rounded-3xl blur-2xl"></div>
                <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8 border border-gray-700">
                  {/* Image Carousel */}
                  <div className="relative overflow-hidden rounded-2xl mb-6">
                    <div 
                      className="flex transition-transform duration-500 ease-in-out"
                      style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                    >
                      {slides.map((slide) => (
                        <img 
                          key={slide.id}
                          src={slide.url} 
                          alt={slide.alt}
                          className="w-full h-64 object-cover flex-shrink-0"
                        />
                      ))}
                    </div>
                    
                    {/* Carousel Controls */}
                    <button 
                      onClick={prevSlide}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                      aria-label="Slide anterior"
                      data-testid="carousel-prev-btn"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button 
                      onClick={nextSlide}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                      aria-label="Próximo slide"
                      data-testid="carousel-next-btn"
                    >
                      <ChevronRight size={20} />
                    </button>
                    
                    {/* Carousel Indicators */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                      {slides.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentSlide(index)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            index === currentSlide ? 'bg-teal-500 w-6' : 'bg-white/50 hover:bg-white/70'
                          }`}
                          aria-label={`Ir para slide ${index + 1}`}
                          data-testid={`carousel-indicator-${index}`}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Updated Stats */}
                  <div className="grid grid-cols-3 gap-4" data-testid="hero-stats">
                    <div className="text-center p-4 bg-gray-800/50 rounded-xl">
                      <div className="text-3xl font-bold text-teal-400" data-testid="stat-years">{heroStats.years}</div>
                      <div className="text-sm text-gray-400">anos de experiência</div>
                    </div>
                    <div className="text-center p-4 bg-gray-800/50 rounded-xl">
                      <div className="text-3xl font-bold text-teal-400" data-testid="stat-clients">{heroStats.clients}</div>
                      <div className="text-sm text-gray-400">clientes satisfeitos</div>
                    </div>
                    <div className="text-center p-4 bg-gray-800/50 rounded-xl">
                      <div className="text-3xl font-bold text-teal-400" data-testid="stat-capacity">{heroStats.capacity}</div>
                      <div className="text-sm text-gray-400">Capacidade</div>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-teal-500 rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-12 bg-gray-50 border-y border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-12">
            <span className="text-gray-500 font-medium">Marcas que trabalhamos:</span>
            {brands.map((brand, index) => (
              <AnimatedSection key={brand.id} animation="scaleUp" delay={index * 100}>
                <Link 
                  to={`/produtos?brand=${brand.name}`}
                  className="text-2xl font-bold text-gray-400 hover:text-teal-500 transition-colors cursor-pointer"
                  data-testid={`brand-link-${brand.name.toLowerCase()}`}
                >
                  {brand.name}
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <span className="text-teal-500 font-semibold uppercase tracking-wider">Nossos Serviços</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2 mb-4">
              Soluções Completas
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Oferecemos uma gama completa de serviços para atender todas as suas necessidades em movimentação de cargas.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Settings;
              return (
                <AnimatedSection key={service.id} animation="fadeUp" delay={index * 100}>
                  <div className="group p-8 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-teal-200 transition-all duration-500" data-testid={`service-card-${service.id}`}>
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-teal-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-gradient-to-br group-hover:from-teal-500 group-hover:to-teal-600 transition-all duration-500">
                      <IconComponent size={28} className="text-teal-600 group-hover:text-white transition-colors duration-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <span className="text-teal-500 font-semibold uppercase tracking-wider">Produtos em Destaque</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2 mb-4">
              Nossa Linha de Empilhadeiras
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Equipamentos de alta qualidade para todas as necessidades da sua operação.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.slice(0, 4).map((product, index) => (
              <AnimatedSection key={product.id} animation="fadeUp" delay={index * 100}>
                <Link 
                  to={`/produtos/${product.id}`}
                  className="group block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500"
                  data-testid={`featured-product-${product.id}`}
                >
                  <div className="relative overflow-hidden">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-teal-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {product.brand}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-sm text-gray-500 mb-3">Modelo: {product.model}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-teal-600 font-semibold">{product.capacity}</span>
                      <ChevronRight size={20} className="text-gray-400 group-hover:text-teal-500 group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection animation="fadeUp" delay={400} className="text-center mt-12">
            <Link
              to="/produtos"
              className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-800 hover:scale-105 transition-all duration-300"
              data-testid="view-all-products-btn"
            >
              Ver Todos os Produtos
              <ArrowRight size={20} />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Differentials Section */}
      <section className="py-24 bg-gray-900 text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-teal-500/10 to-transparent"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <span className="text-teal-400 font-semibold uppercase tracking-wider">Por Que Nos Escolher</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-2 mb-4">
              Nossos Diferenciais
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {companyInfo.experience} e compromisso com a excelência.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {differentials.map((diff, index) => (
              <AnimatedSection key={diff.id} animation="fadeUp" delay={index * 100}>
                <div className="p-8 bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700 hover:border-teal-500/50 transition-all duration-500" data-testid={`differential-card-${diff.id}`}>
                  <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-6">
                    <CheckCircle size={24} className="text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{diff.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{diff.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <span className="text-teal-500 font-semibold uppercase tracking-wider">Depoimentos</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2 mb-4">
              O Que Nossos Clientes Dizem
            </h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <AnimatedSection key={testimonial.id} animation="fadeUp" delay={index * 100}>
                <div className="p-8 bg-gray-50 rounded-2xl border border-gray-100" data-testid={`testimonial-card-${testimonial.id}`}>
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={20} className="fill-teal-500 text-teal-500" />
                    ))}
                  </div>
                  <p className="text-gray-600 italic mb-6">"{testimonial.text}"</p>
                  <div>
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.company}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-gray-50" id="contato-home">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection animation="fadeRight">
              <span className="text-teal-500 font-semibold uppercase tracking-wider">Entre em Contato</span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2 mb-6">
                Solicite seu Orçamento
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Estamos prontos para atender sua empresa com as melhores soluções em empilhadeiras.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center">
                    <Phone size={24} className="text-teal-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Telefone</p>
                    <a href={`tel:${companyInfo.phone}`} className="text-lg font-semibold text-gray-900 hover:text-teal-600 transition-colors" data-testid="contact-phone">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-green-600 fill-current">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">WhatsApp</p>
                    <a href={`https://wa.me/${companyInfo.whatsapp}`} target="_blank" rel="noopener noreferrer" className="text-lg font-semibold text-gray-900 hover:text-green-600 transition-colors" data-testid="contact-whatsapp">
                      {companyInfo.whatsappFormatted}
                    </a>
                  </div>
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection animation="fadeLeft">
              <ContactForm 
                variant="compact"
                title="Envie sua Mensagem"
                subtitle="Responderemos em até 24 horas."
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-teal-500 to-teal-600">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp" className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Pronto para Otimizar sua Logística?
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
              Entre em contato agora e descubra como podemos ajudar sua empresa.
            </p>
            <a
              href={`https://wa.me/${companyInfo.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-xl font-bold hover:bg-gray-100 hover:scale-105 transition-all duration-300 shadow-lg"
              data-testid="cta-whatsapp-btn"
            >
              Fale Conosco
              <ArrowRight size={20} />
            </a>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};

export default HomePage;
